import { createSelector } from 'reselect';
import { pipe, identity } from 'fp-ts/function';
import { array, nonEmptyArray, number, ord, record, string } from 'fp-ts';
import { DateTimeUtils, primitiveMemoize } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { TextKey, CompanionAccommodationExtent } from '@mews-commander/enums';
import { Reservation, Service } from '@mews-commander/data-access';
import { getTextKeyFromServiceOrderState, getNowUtc } from '@mews-commander/utils';
import {
    getCurrentCompanionAccommodationExtent,
    getCurrentEnterpriseSortedReservationsByGroupId,
    getEditableHistoryWindowMilisecondCount,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getReservationLatestUndoCancelationUtc } from '@mews-commander/legacy-entities/src/utils/reservations';
import { getServices } from '@mews-commander/legacy-entities/src/ducks/services';

export interface DateGroup {
    textKey?: Mews.TextKey;
    intervalStartUtc: string | undefined;
    intervalEndUtc: string | undefined;
    reservations: Reservation[];
}

const memoizedUtcToDateString = primitiveMemoize(
    (utc: string, timezone: string) => DateTimeUtils.dateTimeUtcStringToDate(utc, timezone).value
);

const getReservationLocalizedInterval =
    (timezone: string) =>
    ({ startUtc, endUtc }: Reservation) =>
        `${memoizedUtcToDateString(startUtc, timezone)}${memoizedUtcToDateString(endUtc, timezone)}`;

export type GroupingTextKeys = ReturnType<typeof getTextKeyFromServiceOrderState>;
export interface StateGroup {
    dateGroups: DateGroup[];
    textKey: GroupingTextKeys;
}

const ordering: Record<GroupingTextKeys, number> = {
    [TextKey.ConfirmationMissed]: 1,
    [TextKey.ReservationTimeStateCheckInMissed]: 2,
    [TextKey.ReservationTimeStateCheckOutMissed]: 3,

    [TextKey.ServiceOrderStateRequested]: 4,
    [TextKey.ServiceOrderStateInquired]: 5,
    [TextKey.ToBeConfirmed]: 6,

    [TextKey.ReservationTimeStateToBeCheckedIn]: 7,
    [TextKey.OnlineCheckedIn]: 8,
    [TextKey.CheckedIn]: 9,

    [TextKey.ReservationTimeStateToBeCheckedOut]: 10,
    [TextKey.OnlineCheckedOut]: 11,

    [TextKey.ReservationTimeStateFuture]: 12,
    [TextKey.ReservationTimeStatePast]: 13,
    [TextKey.ServiceOrderStateCanceled]: 14,
};
export const groupingTextKeyOrd = ord.contramap((key: GroupingTextKeys) => ordering[key])(number.Ord);
const byStartUrc = ord.contramap(({ startUtc }: Reservation) => startUtc)(string.Ord);
const byEndUtc = ord.contramap(({ endUtc }: Reservation) => endUtc)(string.Ord);
export const createDateGroups = (stateGroupReservations: nonEmptyArray.NonEmptyArray<Reservation>, timezone: string) =>
    pipe(
        stateGroupReservations,
        array.sortBy([byStartUrc, byEndUtc]),
        nonEmptyArray.groupBy(getReservationLocalizedInterval(timezone)),
        record.collect(string.Ord)(
            (_, reservations): DateGroup => ({
                reservations,
                intervalStartUtc: reservations[0].startUtc,
                intervalEndUtc: reservations[0].endUtc,
            })
        )
    );

const isUndoCancellAvailable =
    (millisecondDuration: number, nowUtc: string, services: Record<string, Service>, timezone: string) =>
    (reservation: Reservation) => {
        const latestUndoCancellationUtc = getReservationLatestUndoCancelationUtc(
            reservation,
            timezone,
            services[reservation.serviceId],
            millisecondDuration
        );
        return latestUndoCancellationUtc !== null && nowUtc < latestUndoCancellationUtc;
    };

const createCancelledReservationDateGroup = (
    cancelledReservations: nonEmptyArray.NonEmptyArray<Reservation>,
    milisecondDuration: number,
    nowUtc: string,
    services: Record<string, Service>,
    timezone: string
): DateGroup[] =>
    pipe(
        cancelledReservations,
        array.partition(isUndoCancellAvailable(milisecondDuration, nowUtc, services, timezone)),
        ({ left: reservationsCancelledPernamently, right: reservationsCancellationAvailable }) => {
            const cancellationAvailable: DateGroup | null =
                reservationsCancellationAvailable.length !== 0
                    ? {
                          intervalStartUtc: reservationsCancellationAvailable[0].startUtc,
                          intervalEndUtc: reservationsCancellationAvailable[0].endUtc,
                          textKey: TextKey.ReservationsRevertingAvailable,
                          reservations: reservationsCancellationAvailable,
                      }
                    : null;
            const cancelledPernamently: DateGroup | null =
                reservationsCancelledPernamently.length !== 0
                    ? {
                          intervalStartUtc: reservationsCancelledPernamently[0].startUtc,
                          intervalEndUtc: reservationsCancelledPernamently[0].endUtc,
                          textKey: TextKey.ReservationsPermanentlyCancelled,
                          reservations: reservationsCancelledPernamently,
                      }
                    : null;
            return compactArray([cancellationAvailable, cancelledPernamently]);
        }
    );
export const getReservationGroupNewStateGroups = createSelector(
    getCurrentEnterpriseSortedReservationsByGroupId,
    getCurrentCompanionAccommodationExtent,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getEditableHistoryWindowMilisecondCount,
    getServices,
    (reservations, accomodationExtent: CompanionAccommodationExtent, timezone: string, duration, services) => {
        const nowUtc = getNowUtc();
        const getStateTextKey = ({ serviceOrderState, reservationTimeState, options }: Reservation) =>
            getTextKeyFromServiceOrderState(serviceOrderState, reservationTimeState, accomodationExtent, options);

        return pipe(
            reservations,
            nonEmptyArray.groupBy(getStateTextKey),
            identity<Record<GroupingTextKeys, nonEmptyArray.NonEmptyArray<Reservation>>>,
            record.collect(groupingTextKeyOrd as ord.Ord<string>)(
                (textKey, stateGroupReservations): StateGroup => ({
                    textKey,
                    dateGroups:
                        textKey === TextKey.ServiceOrderStateCanceled
                            ? createCancelledReservationDateGroup(
                                  stateGroupReservations,
                                  duration,
                                  nowUtc,
                                  services,
                                  timezone
                              )
                            : createDateGroups(stateGroupReservations, timezone),
                })
            )
        );
    }
);
